import { useState, useEffect } from 'react';
import {
    EnhancedDialog,
    EnhancedDialogContent,
    EnhancedDialogHeader,
    EnhancedDialogBody,
    EnhancedDialogFooter,
    EnhancedDialogTitle,
    EnhancedDialogDescription
} from '../ui/enhanced-dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { NumberInput } from '../ui/number-input';
import { SupplierProduct } from '../../lib/types';
import { SupplierProductsAPI, ProductAPI } from '../../lib/api';
import { UserPlus, Search, Building2, MapPin, Phone, Loader2, CheckCircle2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { StatusBadge } from '../ui/status-badge';
import { toast } from '../ui/use-toast';

interface AssignSupplierModalProps {
    isOpen: boolean;
    onClose: () => void;
    product: SupplierProduct;
    onSuccess: () => void;
}

interface AvailableSupplier {
    _id: string;
    businessName: string;
    ownerName: string;
    email: string;
    mobile: string;
    verificationStatus: 'pending' | 'verified' | 'rejected';
    isActive: boolean;
    address?: {
        city?: string;
        state?: string;
    };
}

export default function AssignSupplierModal({
    isOpen,
    onClose,
    product,
    onSuccess
}: AssignSupplierModalProps) {
    // Form state
    const [selectedSupplier, setSelectedSupplier] = useState<AvailableSupplier | null>(null);
    const [price, setPrice] = useState<number>(0);
    const [minOrderQuantity, setMinOrderQuantity] = useState<number>(1);
    const [maxQuantity, setMaxQuantity] = useState<number>(1000);

    // UI state
    const [suppliers, setSuppliers] = useState<AvailableSupplier[]>([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Load suppliers when modal opens - MUST be before any early returns
    useEffect(() => {
        if (isOpen && product) {
            loadSuppliers();
            // Pre-fill price from product if available
            if (product.price) {
                setPrice(product.price);
            } else if ((product.product as any)?.sellingPrice) {
                setPrice((product.product as any).sellingPrice);
            } else if (product.product?.basePrice) {
                setPrice(product.product.basePrice);
            }
        }
    }, [isOpen, product]);

    // Don't render if no product data - MUST be after all hooks
    if (!product) {
        return null;
    }

    const loadSuppliers = async (search?: string) => {
        setLoadingSuppliers(true);
        try {
            const response = await SupplierProductsAPI.getAvailableSuppliers({
                limit: 50,
                search: search,
                verificationStatus: 'verified'
            });

            if (response.success && response.data.suppliers) {
                setSuppliers(response.data.suppliers);
            }
        } catch (error) {
            console.error('Error loading suppliers:', error);
            toast({
                title: "Error",
                description: "Failed to load suppliers",
                variant: "destructive"
            });
        } finally {
            setLoadingSuppliers(false);
        }
    };

    const handleSearch = (query: string) => {
        setSearchQuery(query);
        loadSuppliers(query);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedSupplier) {
            toast({
                title: "Error",
                description: "Please select a supplier",
                variant: "destructive"
            });
            return;
        }

        if (price <= 0) {
            toast({
                title: "Error",
                description: "Please enter a valid price",
                variant: "destructive"
            });
            return;
        }

        setSubmitting(true);
        try {
            // Use ProductAPI.updateProductSupplier to assign supplier to the product
            const response = await ProductAPI.updateProductSupplier(product.product._id, {
                supplier: selectedSupplier._id,
                price: price,
                isActive: true,
                locations: []
            });

            if (response.success) {
                toast({
                    title: "Success",
                    description: `Supplier "${selectedSupplier.businessName}" assigned successfully`,
                    variant: "default"
                });
                onSuccess();
                handleClose();
            } else {
                toast({
                    title: "Error",
                    description: response.message || "Failed to assign supplier",
                    variant: "destructive"
                });
            }
        } catch (error: any) {
            console.error('Error assigning supplier:', error);
            toast({
                title: "Error",
                description: error.message || "Failed to assign supplier. Please try again.",
                variant: "destructive"
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setSelectedSupplier(null);
        setPrice(0);
        setMinOrderQuantity(1);
        setMaxQuantity(1000);
        setSearchQuery('');
        onClose();
    };

    const filteredSuppliers = searchQuery
        ? suppliers.filter(s =>
            s.businessName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            s.ownerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            s.mobile.includes(searchQuery)
        )
        : suppliers;

    return (
        <EnhancedDialog open={isOpen} onOpenChange={handleClose}>
            <EnhancedDialogContent size="2xl">
                <EnhancedDialogHeader>
                    <EnhancedDialogTitle className="flex items-center gap-2">
                        <UserPlus className="h-5 w-5" />
                        Assign Supplier to Product
                    </EnhancedDialogTitle>
                    <EnhancedDialogDescription>
                        Assign a supplier to "{product?.product?.name || 'Product'}"
                    </EnhancedDialogDescription>
                </EnhancedDialogHeader>

                <EnhancedDialogBody>
                    <form id="assign-supplier-form" onSubmit={handleSubmit} className="space-y-6">
                        {/* Product Info */}
                        <div className="bg-muted/50 p-4 rounded-lg">
                            <div className="flex items-start gap-4">
                                {product?.product?.images?.[0]?.url && (
                                    <img
                                        src={product.product.images[0].url}
                                        alt={product.product.name}
                                        className="h-16 w-16 rounded-lg object-cover"
                                    />
                                )}
                                <div className="flex-1">
                                    <h4 className="font-semibold">{product?.product?.name}</h4>
                                    <p className="text-sm text-muted-foreground">
                                        Category: {product?.product?.category?.name || 'N/A'}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        Unit: {product?.product?.unit} | Base Price: ₹{product?.product?.basePrice?.toLocaleString() || 'N/A'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Search Suppliers */}
                        <div className="space-y-2">
                            <Label>Search Suppliers</Label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search by business name, owner, or mobile..."
                                    value={searchQuery}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                        </div>

                        {/* Supplier Selection */}
                        <div className="space-y-2">
                            <Label>Select Supplier *</Label>
                            <div className="border rounded-lg max-h-64 overflow-y-auto">
                                {loadingSuppliers ? (
                                    <div className="flex items-center justify-center p-8">
                                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                        <span className="ml-2 text-muted-foreground">Loading suppliers...</span>
                                    </div>
                                ) : filteredSuppliers.length === 0 ? (
                                    <div className="p-8 text-center text-muted-foreground">
                                        No verified suppliers found
                                    </div>
                                ) : (
                                    <div className="divide-y">
                                        {filteredSuppliers.map((supplier) => (
                                            <button
                                                key={supplier._id}
                                                type="button"
                                                onClick={() => setSelectedSupplier(supplier)}
                                                className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${selectedSupplier?._id === supplier._id
                                                    ? 'bg-primary/10 border-l-4 border-primary'
                                                    : ''
                                                    }`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <Building2 className="h-4 w-4 text-muted-foreground" />
                                                            <span className="font-semibold">{supplier.businessName}</span>
                                                            {selectedSupplier?._id === supplier._id && (
                                                                <CheckCircle2 className="h-4 w-4 text-primary" />
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-muted-foreground mt-1">
                                                            Owner: {supplier.ownerName}
                                                        </p>
                                                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                                            <span className="flex items-center gap-1">
                                                                <Phone className="h-3 w-3" />
                                                                {supplier.mobile}
                                                            </span>
                                                            {supplier.address?.city && (
                                                                <span className="flex items-center gap-1">
                                                                    <MapPin className="h-3 w-3" />
                                                                    {supplier.address.city}, {supplier.address.state}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <StatusBadge status={supplier.verificationStatus} />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Price and Quantity Settings */}
                        {selectedSupplier && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                                <div className="space-y-2">
                                    <Label>Price (₹) *</Label>
                                    <NumberInput
                                        value={price}
                                        onChange={setPrice}
                                        min={0}
                                        placeholder="Enter price"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Min Order Quantity</Label>
                                    <NumberInput
                                        value={minOrderQuantity}
                                        onChange={setMinOrderQuantity}
                                        min={1}
                                        placeholder="Min qty"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Max Quantity</Label>
                                    <NumberInput
                                        value={maxQuantity}
                                        onChange={setMaxQuantity}
                                        min={1}
                                        placeholder="Max qty"
                                    />
                                </div>
                            </div>
                        )}
                    </form>
                </EnhancedDialogBody>

                <EnhancedDialogFooter>
                    <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form="assign-supplier-form"
                        disabled={submitting || !selectedSupplier || price <= 0}
                        className="bg-primary"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Assigning...
                            </>
                        ) : (
                            <>
                                <UserPlus className="h-4 w-4 mr-2" />
                                Assign Supplier
                            </>
                        )}
                    </Button>
                </EnhancedDialogFooter>
            </EnhancedDialogContent>
        </EnhancedDialog>
    );
}
